interface UseImageOptions {
  src: string
  srcset?: string
  sizes?: string
  alt?: string
  class?: string
  loading?: HTMLImageElement['loading']
  crossorigin?: string
  referrerPolicy?: HTMLImageElement['referrerPolicy']
}

// Helper function to wait for image to be ready
export function waitForImageReady(image: HTMLImageElement, maxRetries = 10): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    let retries = 0

    const checkReady = () => {
      if (image.complete && image.naturalWidth > 0 && image.naturalHeight > 0) {
        console.warn('Image ready after retry:', name, image.naturalWidth, image.naturalHeight)
        resolve(image)
      } else if (retries < maxRetries) {
        retries++
        console.warn(`Retry ${retries}/${maxRetries} for image:`, name, image.complete, image.naturalWidth, image.naturalHeight)
        setTimeout(checkReady, 100)
      } else {
        reject(new Error(`Image not ready after ${maxRetries} retries: ${name}`))
      }
    }

    checkReady()
  })
}

function imgToDataUrl(img: HTMLImageElement) {
  // Check if image has valid dimensions
  if (!img.width || !img.height || img.width <= 0 || img.height <= 0) {
    console.warn('Image has invalid dimensions, returning original image', img.width, img.height)
    return img
  }

  try {
    const newImg = new Image()
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    if (!context) {
      console.warn('Could not get 2D context, returning original image')
      return img
    }

    canvas.width = img.width
    canvas.height = img.height
    canvas.textContent = img.alt

    // Try to draw the image on canvas
    context.drawImage(img, 0, 0)

    const dataUrl = canvas.toDataURL('image/png')
    newImg.src = dataUrl

    // Store in localStorage only if successful
    try {
      localStorage.setItem('savedImageData', dataUrl)
    } catch (e) {
      console.warn('Could not save image to localStorage:', e)
    }

    return newImg
  } catch (e) {
    console.warn('Error converting image to data URL, returning original image:', e)
    return img
  }
}

export async function loadImage(options: UseImageOptions): Promise<HTMLImageElement | string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const {
      src,
      srcset,
      sizes,
      class: clazz,
      loading,
      crossorigin,
      referrerPolicy,
    } = options

    if (srcset) {
      img.srcset = srcset
    }
    if (sizes) {
      img.sizes = sizes
    }
    if (clazz) {
      img.className = clazz
    }
    if (loading) {
      img.loading = loading
    }
    if (referrerPolicy) {
      img.referrerPolicy = referrerPolicy
    }

    img.onerror = (e) => {
      console.warn('Failed to load image:', src, e)
      reject(new Error(`Failed to load image: ${src}`))
    }

    // Helper function to check if image is ready
    const checkImageReady = () => {
      // Wait a bit more to ensure image is fully loaded
      setTimeout(() => {
        if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
          console.warn('Image is ready:', src, img.naturalWidth, img.naturalHeight)
          resolve(img)
        } else {
          console.warn('Image not ready yet, retrying:', src, img.complete, img.naturalWidth, img.naturalHeight)
          // Retry after a short delay
          setTimeout(checkImageReady, 50)
        }
      }, 10)
    }

    // check if //domain.com or http://domain.com is a different origin
    if (/^\w+:?\/\//.test(src) && !src.startsWith(location.origin)) {
      img.crossOrigin = 'Anonymous'
      img.onload = () => {
        // Verify image is properly loaded with valid dimensions
        if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
          try {
            const imgCors = imgToDataUrl(img)
            resolve(imgCors)
          } catch (e) {
            console.warn('Error processing cross-origin image, returning original:', e)
            resolve(img)
          }
        } else {
          console.warn('Cross-origin image not properly loaded, using fallback')
          checkImageReady()
        }
      }

      // launch the loading
      img.src = `${src}?${Date.now()}` // to avoid caching
      return
    }

    if (crossorigin) {
      img.crossOrigin = crossorigin
    }

    img.onload = () => {
      // Verify image is properly loaded
      if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
        resolve(img)
      } else {
        console.warn('Image not properly loaded, using fallback')
        checkImageReady()
      }
    }
    // launch the loading
    img.src = src
  })
}
